import { languageForPath } from '@/shared/lib/code-highlight';
import { CodeLines } from '@/shared/ui/code-lines';
import { Pill } from '@/shared/ui/pill';

import { reviewSections } from '../review-presentation';

import type { ReviewEvidence, WorkshopEvaluation } from '@mio/shared';

const resultLabels = {
  met: 'Demonstrated',
  partial: 'Partly demonstrated',
  not_met: 'Not yet demonstrated',
};

function citationKey(item: ReviewEvidence) {
  return item.kind === 'source'
    ? `source:${item.path}:${item.startLine}-${item.endLine}:${item.quote}`
    : `explanation:${item.quote}`;
}

function citationLabel(item: ReviewEvidence) {
  return item.kind === 'source'
    ? `Submitted ${item.path}, ${item.startLine === item.endLine ? `line ${item.startLine}` : `lines ${item.startLine}–${item.endLine}`}`
    : 'Submitted explanation';
}

/**
 * A quote is expanded the first time the review cites it. Later sections that
 * cite the same lines point back to it in one line instead of repeating it.
 */
function Citations({ items, seen }: { items: ReviewEvidence[]; seen: Set<string> }) {
  if (items.length === 0) return null;
  const fresh: ReviewEvidence[] = [];
  const repeated: ReviewEvidence[] = [];
  for (const item of items) {
    const key = citationKey(item);
    if (seen.has(key)) {
      if (!repeated.some((existing) => citationKey(existing) === key)) repeated.push(item);
      continue;
    }
    seen.add(key);
    fresh.push(item);
  }
  return (
    <div className="mt-2 space-y-1">
      {fresh.map((item) => (
        <details key={citationKey(item)} className="border-line rounded border p-2">
          <summary className="cursor-pointer text-xs">{citationLabel(item)}</summary>
          <pre className="code-highlight mt-2 overflow-x-auto whitespace-pre-wrap break-words text-xs leading-5">
            {item.kind === 'source' ? (
              <CodeLines
                code={item.quote}
                language={languageForPath(item.path)}
                lineNumbers
                firstLine={item.startLine}
              />
            ) : (
              item.quote
            )}
          </pre>
        </details>
      ))}
      {repeated.length ? (
        <p className="text-xs">
          Cited above:{' '}
          {repeated.map((item) => citationLabel(item).replace(/^Submitted /, '')).join('; ')}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Every stored review reads in the same order: what was completed, what is not
 * done, which understanding gaps explain it, and what to do next. Text is
 * rendered as text, and citations always describe the frozen submission.
 */
export function WorkshopFeedback({ result }: { result: WorkshopEvaluation }) {
  const sections = reviewSections(result);
  // One registry per render: the same quote is expanded once for the whole review.
  const seen = new Set<string>();
  const advisoryCount = sections.criteria.filter((item) => item.advisory).length;
  return (
    <div className="text-fg-muted mt-4 space-y-6 text-sm">
      <p className="text-xs">
        AI review of saved code and explanation. Code execution was not verified.
      </p>
      <p className="text-fg leading-6">{result.feedback}</p>
      {sections.criteria.length ? (
        <section aria-label="Criteria at a glance" className="space-y-2">
          {sections.required ? (
            <p className="text-fg font-medium">
              Required final criteria fully met: {sections.required.met} / {sections.required.total}
              {advisoryCount
                ? ` · ${advisoryCount} advisory ${advisoryCount === 1 ? 'criterion does' : 'criteria do'} not block completion`
                : ''}
            </p>
          ) : null}
          <ul className="flex flex-wrap gap-2">
            {sections.criteria.map((item) => (
              <li key={item.id}>
                <Pill
                  tone={
                    item.result === 'met'
                      ? 'success'
                      : item.result === 'not_met'
                        ? 'warning'
                        : 'neutral'
                  }
                  title={item.advisory ? 'Advisory criterion' : 'Required criterion'}
                >
                  {item.label} · {resultLabels[item.result]}
                  {item.advisory ? ' · Advisory' : ''}
                </Pill>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <section aria-label="Progress demonstrated">
        <h3 className="text-fg font-medium">Progress demonstrated</h3>
        {sections.completed.length ? (
          <ul className="mt-2 space-y-3">
            {sections.completed.map((item) => (
              <li key={item.id}>
                <p className="text-fg">
                  {item.label}
                  {item.advisory ? ' · Advisory' : ''}
                </p>
                <p>{item.text}</p>
                <Citations items={item.citations} seen={seen} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2">
            No complete final criterion yet.
            {sections.partialProgress.length
              ? ' Working portions are acknowledged below.'
              : ' The review needs further implementation or evidence.'}
          </p>
        )}
        {sections.partialProgress.length ? (
          <ul className="mt-3 space-y-3">
            {sections.partialProgress.map((item) => (
              <li key={item.id}>
                <p className="text-fg">
                  {item.label} · Partly demonstrated{item.advisory ? ' · Advisory' : ''}
                </p>
                <p>{item.text}</p>
                <Citations items={item.citations} seen={seen} />
              </li>
            ))}
          </ul>
        ) : null}
      </section>
      <section aria-label="What's not done yet">
        <h3 className="text-fg font-medium">What’s not done yet</h3>
        {sections.notDone.length ? (
          <ul className="mt-2 space-y-3">
            {sections.notDone.map((item) => (
              <li key={item.id}>
                <p className="text-fg">
                  {item.label} · {resultLabels[item.result]}
                  {item.advisory ? ' · Advisory' : ''}
                </p>
                <p>{item.missing}</p>
                {item.result !== 'partial' && item.missing !== item.text ? (
                  <p className="mt-1 text-xs">{item.text}</p>
                ) : null}
                <Citations items={item.citations} seen={seen} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2">Every criterion is demonstrated.</p>
        )}
      </section>
      <section aria-label="What to investigate next">
        <h3 className="text-fg font-medium">What to investigate next</h3>
        {sections.understandingGaps.length ? (
          <ul className="mt-2 space-y-4">
            {sections.understandingGaps.map((gap, index) => (
              <li key={index}>
                <p>
                  {gap.concept}: {gap.evidence}
                </p>
                {gap.whyItMatters ? <p className="mt-1">{gap.whyItMatters}</p> : null}
                {gap.question ? <p className="text-fg mt-2 font-medium">{gap.question}</p> : null}
                <Citations items={gap.citations} seen={seen} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2">No further investigation was requested by this review.</p>
        )}
      </section>
      <section aria-label="What to do next">
        <h3 className="text-fg font-medium">What to do next</h3>
        {sections.nextActions.length ? (
          <ol className="mt-2 list-decimal space-y-1 pl-5">
            {sections.nextActions.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ol>
        ) : (
          <p className="mt-2">
            {sections.outcome === 'passed'
              ? 'Compare your approach with the reference solution, then continue to the next activity.'
              : 'Work through the items above, then revise and resubmit.'}
          </p>
        )}
      </section>
      {sections.solutionNotes ? (
        <section aria-label="How your submission differs from the reference">
          <h3 className="text-fg font-medium">How your submission differs from the reference</h3>
          <p className="mt-2 whitespace-pre-wrap leading-6">{sections.solutionNotes}</p>
        </section>
      ) : null}
    </div>
  );
}
